import {reactFetchJSON} from '@github-ui/verified-fetch'

export interface RepoFileTree {
  paths: string[]
  directories: string[]
  ref?: string
  commitOID?: string
}

/**
 * Fetches the file tree for a repository's default branch. Resolves the default
 * branch via the copilot repo details endpoint, then uses the tree-list endpoint
 * (same one code-view and copilot-spaces use) to list all files and directories.
 */
export async function fetchRepoFiles(repoId: number, nwo: string, signal?: AbortSignal): Promise<RepoFileTree> {
  const [owner, repo] = nwo.split('/')
  if (!owner || !repo) throw new Error(`Invalid nwo: ${nwo}`)

  // Resolve commit OID via the copilot repo details endpoint
  const repoResponse = await reactFetchJSON(`/github-copilot/chat/repositories/${repoId}`, {method: 'GET', signal})
  if (!repoResponse.ok) {
    throw new Error(`Failed to fetch repository details for ${nwo} (${repoResponse.status})`)
  }
  const repoData = (await repoResponse.json()) as {commitOID?: string; ref?: string; defaultBranch?: string}
  const commitOid = repoData.commitOID
  if (!commitOid) {
    throw new Error(`Repository details missing commit OID for ${nwo}`)
  }

  // Fetch tree list using the commit OID as the ref
  const treeResponse = await reactFetchJSON(`/${owner}/${repo}/tree-list/${commitOid}?include_directories=true`, {
    method: 'GET',
    signal,
  })
  if (!treeResponse.ok) {
    throw new Error(`Failed to load file tree for ${nwo} (${treeResponse.status})`)
  }

  const data = (await treeResponse.json()) as {paths?: string[]; directories?: string[]}
  return {
    paths: data.paths ?? [],
    directories: data.directories ?? [],
    ref: repoData.ref || repoData.defaultBranch || commitOid,
    commitOID: commitOid,
  }
}
